import type { DeclaredParam } from "./paramSchema";
import { input } from "./ui";

// The dialog opens on the declared defaults rather than on empty fields. An omitted parameter falls
// through to whatever the node's own expression resolves to, which is not always the default the
// trigger advertises — so showing the default and sending it is the only way the dialog tells the
// truth about what the run will do.
export const initialValues = (
	declared: DeclaredParam[],
): Record<string, string> => {
	const out: Record<string, string> = {};
	for (const param of declared) {
		if (param.default === undefined || param.default === null) continue;
		out[param.key] = Array.isArray(param.default)
			? param.default.join(",")
			: String(param.default);
	}
	return out;
};

export const ParamField = ({
	param,
	value,
	onChange,
}: {
	param: DeclaredParam;
	value: string;
	onChange: (next: string) => void;
}) => {
	const options = param.options ?? [];

	if (param.type === "boolean") {
		return (
			<label className="flex items-start gap-2">
				<input
					type="checkbox"
					checked={value === "true"}
					onChange={(event) => onChange(String(event.target.checked))}
					className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary"
				/>
				<span>
					<span className="block text-[12px] font-medium text-slate-600">
						{param.label ?? param.key}
					</span>
					{param.hint && (
						<span className="block text-[11px] text-slate-500">
							{param.hint}
						</span>
					)}
				</span>
			</label>
		);
	}

	if (param.type === "multiselect") {
		const selected = new Set(value.split(",").filter(Boolean));
		return (
			<div className="space-y-1">
				<span className="block text-[12px] font-medium text-slate-600">
					{param.label ?? param.key}
				</span>
				<div className="flex flex-wrap gap-1.5">
					{options.map((option) => {
						const on = selected.has(option.value);
						return (
							<button
								key={option.value}
								type="button"
								onClick={() => {
									const next = new Set(selected);
									if (on) next.delete(option.value);
									else next.add(option.value);
									onChange(
										options
											.map((o) => o.value)
											.filter((v) => next.has(v))
											.join(","),
									);
								}}
								className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
									on
										? "bg-primary text-white"
										: "bg-slate-100 text-slate-600 hover:bg-slate-200"
								}`}
							>
								{option.label ?? option.value}
							</button>
						);
					})}
				</div>
				{param.hint && (
					<span className="block text-[11px] text-slate-500">{param.hint}</span>
				)}
			</div>
		);
	}

	if (param.type === "select") {
		return (
			<label className="block space-y-1">
				<span className="block text-[12px] font-medium text-slate-600">
					{param.label ?? param.key}
				</span>
				<select
					className={input}
					value={value}
					onChange={(event) => onChange(event.target.value)}
				>
					{options.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label ?? option.value}
						</option>
					))}
				</select>
				{param.hint && (
					<span className="block text-[11px] text-slate-500">{param.hint}</span>
				)}
			</label>
		);
	}

	return (
		<label className="block space-y-1">
			<span className="block text-[12px] font-medium text-slate-600">
				{param.label ?? param.key}
			</span>
			<input
				className={input}
				type={param.type === "number" ? "number" : "text"}
				value={value}
				onChange={(event) => onChange(event.target.value)}
			/>
			{param.hint && (
				<span className="block text-[11px] text-slate-500">{param.hint}</span>
			)}
		</label>
	);
};
